import React from 'react';

import { Card, CircularProgress, Grid, Typography } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';

import { BenefitType, DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { parseBooleanToString, parseNullableValue } from '@clearverify/utils';

import { EmploymentDataInfoFragment } from 'src/api';
import { SxProp } from 'src/types/mui';
import { getEmploymentStatusLabel } from 'src/utils/employee';
import { hasBenefitDentalFields, hasBenefitMedicalFields } from 'src/utils/employmentData';

import { EmploymentDataHistoricalIncomeBlock } from './components/EmploymentDataHistoricalIncomeBlock';
import { EmploymentDataSummaryIncomeBlock } from './components/EmploymentDataSummaryIncomeBlock';

interface EmploymentDataDetailsProps {
  loading?: boolean;
  employmentData: Nullable<EmploymentDataInfoFragment>;
}

interface InformationFieldType {
  label: string;
  value: string | number;
}

export const EmploymentDataDetails: React.FC<EmploymentDataDetailsProps> = ({
  employmentData,
  loading,
}) => {
  const {
    companyInformation,
    employeeInformation,
    headquartersAddress,
    positionInformation,
    summaryIncomeInformation,
    historicalIncomeInformation,
    benefitsInformation,
  } = employmentData ?? {};

  // Company Information
  const companyRegisteredBusinessName =
    companyInformation?.registeredBusinessName || DEFAULT_VALUE_IF_NIL;
  const fein = companyInformation?.fein || DEFAULT_VALUE_IF_NIL;
  const locationName = companyInformation?.locationName || DEFAULT_VALUE_IF_NIL;
  const extLocationId = companyInformation?.extLocationId || DEFAULT_VALUE_IF_NIL;
  const locationAddress = companyInformation?.locationAddress || DEFAULT_VALUE_IF_NIL;

  const companyInformationFields: InformationFieldType[] = [
    {
      label: 'Registered Business Name',
      value: companyRegisteredBusinessName,
    },
    { label: 'FEIN', value: fein },
    { label: 'Location Name', value: locationName },
    { label: 'External Location ID', value: extLocationId },
    {
      label: 'Location Address',
      value: locationAddress,
    },
  ];

  // Headquarters
  const street = headquartersAddress?.street || DEFAULT_VALUE_IF_NIL;
  const city = headquartersAddress?.city || DEFAULT_VALUE_IF_NIL;
  const state = headquartersAddress?.state || DEFAULT_VALUE_IF_NIL;
  const zipCode = headquartersAddress?.zipCode || DEFAULT_VALUE_IF_NIL;

  const headquartersAddressFields: InformationFieldType[] = [
    { label: 'Street', value: street },
    { label: 'City', value: city },
    { label: 'State', value: state },
    { label: 'Zip Code', value: zipCode },
  ];

  // Position Information
  const positionRegisteredBusinessName =
    positionInformation?.registeredBusinessName || DEFAULT_VALUE_IF_NIL;
  const title = positionInformation?.title || DEFAULT_VALUE_IF_NIL;
  const division = positionInformation?.division || DEFAULT_VALUE_IF_NIL;

  const positionInformationFields: InformationFieldType[] = [
    {
      label: 'Registered Business Name',
      value: positionRegisteredBusinessName,
    },
    { label: 'Position title', value: title },
    { label: 'Division', value: division },
  ];

  // Employee Information
  const fullName = employeeInformation?.fullName || DEFAULT_VALUE_IF_NIL;
  const employeeId = employeeInformation?.employeeId || DEFAULT_VALUE_IF_NIL;
  const employmentType = employeeInformation?.employmentType || DEFAULT_VALUE_IF_NIL;
  const employmentStatus = getEmploymentStatusLabel(employeeInformation?.employmentStatus);
  const originalHireDate = employeeInformation?.originalHireDate;
  const mostRecentEmployment = employeeInformation?.mostRecentEmployment || DEFAULT_VALUE_IF_NIL;
  const totalTimeWithEmployer = employeeInformation?.totalTimeWithEmployer || DEFAULT_VALUE_IF_NIL;
  const rateOfPay = employeeInformation?.rateOfPay || DEFAULT_VALUE_IF_NIL;
  const payFrequency = employeeInformation?.payFrequency || DEFAULT_VALUE_IF_NIL;
  const averageHoursPerPayPeriod =
    employeeInformation?.averageHoursPerPayPeriod || DEFAULT_VALUE_IF_NIL;

  const employeeInformationFields: InformationFieldType[] = [
    { label: 'Employee Name', value: fullName },
    { label: 'Employment Status', value: employmentStatus },
    { label: 'Employment Type', value: employmentType },
    { label: 'Employee ID', value: employeeId },

    ...(originalHireDate ? [{ label: 'Original Hire Date', value: originalHireDate }] : []),
    {
      label: originalHireDate ? 'Most Recent Employment' : 'Employment Period',
      value: mostRecentEmployment,
    },

    { label: 'Total time with Employer', value: totalTimeWithEmployer },
    { label: 'Rate of Pay', value: rateOfPay },
    { label: 'Pay Frequency', value: payFrequency },
    {
      label: 'Average Hours Per Pay Period',
      value: averageHoursPerPayPeriod,
    },
  ];

  const benefitsInformationFieldsFirstRow = ({
    carrier,
    type,
  }: {
    type: BenefitType;
    carrier: Nullable<string>;
  }) => {
    return [
      {
        label: 'Enrollment Type',
        value: String(type),
      },
      {
        label: 'Carrier',
        value: parseNullableValue(carrier),
      },
    ];
  };
  const benefitsInformationFieldsSecondRow = ({
    eligible,
    enrolled,
  }: {
    eligible: Nullable<boolean>;
    enrolled: Nullable<boolean>;
  }) => {
    return [
      {
        label: 'Employee Eligible',
        value: parseBooleanToString(eligible),
      },
      {
        label: 'Employee Enrolled',
        value: parseBooleanToString(enrolled),
      },
    ];
  };

  const renderFields = (fieldsList: InformationFieldType[]) => {
    return fieldsList.map(field => (
      <React.Fragment key={field.label}>
        <Grid size={6}>
          <Typography variant="body1" color="text.secondary">
            {field.label}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="body1">{field.value}</Typography>
        </Grid>
      </React.Fragment>
    ));
  };

  return loading ? (
    <Grid container justifyContent="center">
      <CircularProgress />
    </Grid>
  ) : (
    <React.Fragment>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Typography variant="h6">Company Information</Typography>
        </Grid>
        {renderFields(companyInformationFields)}
      </Grid>

      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Typography variant="h6">Employer Headquarters Address</Typography>
        </Grid>
        {renderFields(headquartersAddressFields)}
      </Grid>

      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Typography variant="h6">Position Information</Typography>
        </Grid>
        {renderFields(positionInformationFields)}
      </Grid>

      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Typography variant="h6">Employee Information</Typography>
        </Grid>
        {renderFields(employeeInformationFields)}
      </Grid>
      {!!benefitsInformation?.count && (
        <Grid container spacing={2} mb={4}>
          <Grid size={12}>
            <Typography variant="h6">Benefits Information</Typography>
          </Grid>
          <Grid size={12}>
            {benefitsInformation.items.map(benefit => {
              return (
                <React.Fragment key={benefit.id}>
                  {hasBenefitDentalFields(benefit) && (
                    <Card variant="outlined" sx={cardBenefitStyles}>
                      <Grid container spacing={2}>
                        <Grid container spacing={1} size={6}>
                          {renderFields(
                            benefitsInformationFieldsFirstRow({
                              type: BenefitType.Dental,
                              carrier: benefit.dentalInsuranceCarrier,
                            }),
                          )}
                        </Grid>
                        <Grid container spacing={1} size={6}>
                          {renderFields(
                            benefitsInformationFieldsSecondRow({
                              eligible: benefit.eligibleForDental,
                              enrolled: benefit.enrolledInDental,
                            }),
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  )}
                  {hasBenefitMedicalFields(benefit) && (
                    <Card variant="outlined" sx={cardBenefitStyles}>
                      <Grid container spacing={2}>
                        <Grid container spacing={1} size={6}>
                          {renderFields(
                            benefitsInformationFieldsFirstRow({
                              type: BenefitType.Medical,
                              carrier: benefit.medicalInsuranceCarrier,
                            }),
                          )}
                        </Grid>
                        <Grid container spacing={1} size={6}>
                          {renderFields(
                            benefitsInformationFieldsSecondRow({
                              eligible: benefit.eligibleForMedical,
                              enrolled: benefit.enrolledInMedical,
                            }),
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  )}
                </React.Fragment>
              );
            })}
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Typography variant="h6">Summary Income Information</Typography>
        </Grid>
        {isEmpty(summaryIncomeInformation) ? (
          <Grid size={12}>
            <Typography variant="body2">Income data has not been provided.</Typography>
          </Grid>
        ) : (
          summaryIncomeInformation?.map((payrollItem, index) => {
            return (
              <Grid key={payrollItem.year} mt={index > 0 ? 3 : undefined}>
                <EmploymentDataSummaryIncomeBlock payrollItem={payrollItem} />
              </Grid>
            );
          })
        )}
      </Grid>
      <Grid container spacing={2} mb={4} position="relative">
        <Grid size={12}>
          <Typography variant="h6">Historical Income Information</Typography>
        </Grid>
        {isEmpty(historicalIncomeInformation?.items) ? (
          <Grid size={12}>
            <Typography variant="body2">Income data has not been provided.</Typography>
          </Grid>
        ) : (
          <Grid size={12}>
            <EmploymentDataHistoricalIncomeBlock
              payrollItems={historicalIncomeInformation?.items}
            />
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

const cardBenefitStyles: SxProp = {
  marginBottom: '16px',
  boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
  padding: '24px',
};
